import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import {
  Zap, Bot, ClipboardList, Building2, Briefcase, Users, ShieldCheck, Phone,
  Search, Mail, Database, Plus, Pencil, Trash2, X, Sparkles,
} from "lucide-react";

const STATUS_LABELS = {
  actif: "Actif", prospect: "Prospect", inactif: "Inactif",
  ouvert: "Ouvert", en_cours: "En cours", comble: "Comblé", ferme: "Fermé",
  disponible: "Disponible", en_processus: "En processus", place: "Placé",
  preparation: "Préparation", deposee: "Déposée", en_analyse: "En analyse", approuvee: "Approuvée", refusee: "Refusée",
  nouveau: "Nouveau", contacte: "Contacté", qualifie: "Qualifié", converti: "Converti", perdu: "Perdu",
  brouillon: "Brouillon", envoye: "Envoyé", repondu: "Répondu",
  a_faire: "À faire", conforme: "Conforme", non_conforme: "Non conforme",
};

const ENTITIES = {
  employers: {
    entity: "employers", label: "Employeur", statuses: ["actif", "prospect", "inactif"],
    title: (r) => r.name, subtitle: (r) => [r.sector, r.city, r.contact_name].filter(Boolean).join(" · "),
    fields: [
      { name: "name", label: "Nom de l'entreprise", required: true },
      { name: "contact_name", label: "Contact" },
      { name: "email", label: "Courriel", type: "email" },
      { name: "phone", label: "Téléphone" },
      { name: "sector", label: "Secteur" },
      { name: "city", label: "Ville" },
      { name: "country", label: "Pays" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  positions: {
    entity: "positions", label: "Poste", statuses: ["ouvert", "en_cours", "comble", "ferme"],
    title: (r) => r.title, subtitle: (r) => [r.location, r.salary_range].filter(Boolean).join(" · "),
    fields: [
      { name: "title", label: "Titre du poste", required: true },
      { name: "employer_id", label: "Employeur", ref: "employers", refLabel: (r) => r.name },
      { name: "location", label: "Lieu" },
      { name: "salary_range", label: "Salaire" },
      { name: "requirements", label: "Exigences", type: "textarea" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  candidates: {
    entity: "candidates", label: "Candidat", statuses: ["disponible", "en_processus", "place"],
    title: (r) => r.name, subtitle: (r) => [r.occupation, r.cnp_code && `CNP ${r.cnp_code}`, r.nationality].filter(Boolean).join(" · "),
    fields: [
      { name: "name", label: "Nom complet", required: true },
      { name: "email", label: "Courriel", type: "email" },
      { name: "phone", label: "Téléphone" },
      { name: "nationality", label: "Nationalité" },
      { name: "occupation", label: "Métier / profession" },
      { name: "cnp_code", label: "Code CNP" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  "eimt-cases": {
    entity: "eimt-cases", label: "Dossier EIMT", statuses: ["preparation", "deposee", "en_analyse", "approuvee", "refusee"],
    title: (r) => r.reference, subtitle: (r) => [r.submitted_at && `déposée ${r.submitted_at}`, r.decided_at && `décision ${r.decided_at}`].filter(Boolean).join(" · "),
    fields: [
      { name: "reference", label: "Référence du dossier", required: true },
      { name: "candidate_id", label: "Candidat", ref: "candidates", refLabel: (r) => r.name },
      { name: "employer_id", label: "Employeur", ref: "employers", refLabel: (r) => r.name },
      { name: "position_id", label: "Poste", ref: "positions", refLabel: (r) => r.title },
      { name: "submitted_at", label: "Date de dépôt", type: "date" },
      { name: "decided_at", label: "Date de décision", type: "date" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  prospects: {
    entity: "prospects", label: "Prospect", statuses: ["nouveau", "contacte", "qualifie", "converti", "perdu"],
    title: (r) => r.company_name, subtitle: (r) => [r.sector, r.source].filter(Boolean).join(" · "),
    fields: [
      { name: "company_name", label: "Entreprise", required: true },
      { name: "sector", label: "Secteur" },
      { name: "source", label: "Source" },
      { name: "contact_info", label: "Coordonnées" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  outreach: {
    entity: "outreach", label: "Prospection", statuses: ["brouillon", "envoye", "repondu"],
    title: (r) => r.subject || r.message?.slice(0, 60), subtitle: (r) => r.channel,
    fields: [
      { name: "channel", label: "Canal", required: true, type: "select", options: ["courriel", "telephone", "linkedin"] },
      { name: "subject", label: "Objet" },
      { name: "message", label: "Message", type: "textarea", required: true },
      { name: "prospect_id", label: "Prospect", ref: "prospects", refLabel: (r) => r.company_name },
      { name: "employer_id", label: "Employeur", ref: "employers", refLabel: (r) => r.name },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  compliance: {
    entity: "compliance", label: "Conformité", statuses: ["a_faire", "en_cours", "conforme", "non_conforme"],
    title: (r) => r.requirement, subtitle: (r) => [r.entity_name, r.due_date && `échéance ${r.due_date}`].filter(Boolean).join(" · "),
    fields: [
      { name: "entity_type", label: "Type d'entité", required: true, type: "select", options: ["employeur", "candidat", "eimt"] },
      { name: "entity_name", label: "Entité concernée", required: true },
      { name: "requirement", label: "Exigence", required: true },
      { name: "due_date", label: "Échéance", type: "date" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  calls: {
    entity: "calls", label: "Appel IA", statuses: [],
    title: (r) => r.entity_name, subtitle: (r) => [r.summary?.slice(0, 70), r.outcome].filter(Boolean).join(" · "),
    fields: [
      { name: "entity_type", label: "Type", required: true, type: "select", options: ["candidat", "employeur", "prospect"] },
      { name: "entity_name", label: "Contact", required: true },
      { name: "summary", label: "Résumé de l'appel", type: "textarea", required: true },
      { name: "outcome", label: "Résultat" },
      { name: "called_at", label: "Date de l'appel", type: "date" },
    ],
  },
};

const MODULES = [
  { key: "command-center", label: "Command Center", icon: Zap },
  { key: "autopilot", label: "Autopilot", icon: Bot },
  { key: "eimt", label: "Suivi EIMT", icon: ClipboardList, cfg: "eimt-cases" },
  { key: "employers", label: "Employeurs", icon: Building2, cfg: "employers" },
  { key: "positions", label: "Postes", icon: Briefcase, cfg: "positions" },
  { key: "candidates", label: "Candidats", icon: Users, cfg: "candidates" },
  { key: "compliance", label: "Immigration & Conformité", icon: ShieldCheck, cfg: "compliance" },
  { key: "calls", label: "Appels IA", icon: Phone, cfg: "calls", suggest: true },
  { key: "prospects", label: "Découverte Employeurs", icon: Search, cfg: "prospects" },
  { key: "outreach", label: "Prospection", icon: Mail, cfg: "outreach" },
  { key: "banque", label: "Banque de candidats", icon: Database, cfg: "candidates", fixedStatus: "disponible" },
];

const StatusBadge = ({ status }) =>
  status ? (
    <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 shrink-0 ${
      ["actif", "disponible", "approuvee", "conforme", "payee", "converti", "repondu"].includes(status) ? "bg-green-50 text-green-700"
      : ["refusee", "non_conforme", "perdu", "inactif"].includes(status) ? "bg-red-50 text-red-600"
      : "bg-slate-100 text-slate-600"
    }`}>
      {STATUS_LABELS[status] || status}
    </span>
  ) : null;

const CommandCenter = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/recruitment/overview/stats").then((r) => setStats(r.data)).catch((e) => setError(formatApiError(e)));
  }, []);
  if (error) return <div data-testid="rec-overview-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>;
  if (!stats) return <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" /></div>;
  const cards = [
    { key: "employeurs", label: "Employeurs actifs" },
    { key: "postes_ouverts", label: "Postes ouverts" },
    { key: "candidats", label: "Candidats" },
    { key: "banque_disponibles", label: "Banque disponible" },
    { key: "eimt_actifs", label: "Dossiers EIMT actifs" },
    { key: "prospects", label: "Prospects" },
    { key: "conformite_a_faire", label: "Conformité à traiter" },
    { key: "appels", label: "Appels IA" },
  ];
  return (
    <div data-testid="rec-command-center" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.key} data-testid={`rec-stat-${c.key}`} className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{c.label}</p>
            <p className="text-2xl font-bold text-brand-navy mt-1">{stats[c.key]}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-semibold text-brand-navy">Dossiers EIMT récents</h3></div>
        {stats.eimt_recents.length === 0 ? (
          <p data-testid="rec-eimt-empty" className="px-5 py-8 text-center text-sm text-slate-400">Aucun dossier EIMT.</p>
        ) : (
          stats.eimt_recents.map((c) => (
            <div key={c.record_id} data-testid={`rec-eimt-recent-${c.record_id}`} className="flex items-center gap-3 px-5 py-3 border-b border-slate-50">
              <span className="text-sm font-medium text-brand-navy flex-1">{c.reference}</span>
              <StatusBadge status={c.status} />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const Autopilot = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/recruitment/autopilot/logs").then((r) => setData(r.data)).catch(() => setData({ enabled: false, rules: [], logs: [] }));
  }, []);
  if (!data) return <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" /></div>;
  return (
    <div data-testid="rec-autopilot" className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-3">
        <Bot className="w-5 h-5 text-brand-turquoise" />
        <div>
          <p className="font-semibold text-brand-navy">Automatisations</p>
          <p className="text-sm text-slate-500">
            {data.enabled ? "Autopilot actif." : "Autopilot inactif — les règles s'exécuteront une fois activées."}
          </p>
        </div>
        <span data-testid="autopilot-status" className={`ml-auto text-xs font-semibold rounded-full px-3 py-1 ${data.enabled ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
          {data.enabled ? "Actif" : "Inactif"}
        </span>
      </div>
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-semibold text-brand-navy">Journal d'exécution</h3></div>
        {data.logs.length === 0 ? (
          <p data-testid="autopilot-logs-empty" className="px-5 py-8 text-center text-sm text-slate-400">Aucune exécution enregistrée.</p>
        ) : (
          data.logs.map((l, i) => (
            <div key={i} className="px-5 py-3 border-b border-slate-50 text-sm text-slate-600">
              <span className="font-medium text-brand-navy">{l.rule}</span> — {l.detail}
              <span className="text-xs text-slate-400 ml-2">{l.created_at ? new Date(l.created_at).toLocaleString("fr-CA") : ""}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const SuggestDialog = ({ onClose }) => {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const run = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/recruitment/calls/suggest", { entity_name: name, purpose });
      setDraft(res.data.draft);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="call-suggest-dialog" className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-brand-navy flex items-center gap-2"><Sparkles className="w-4 h-4 text-brand-turquoise" /> Script d'appel IA</h2>
          <button data-testid="call-suggest-close" onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-brand-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Brouillon IA à valider par un humain — aucun appel automatique.</p>
          {error && <div data-testid="call-suggest-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
          <input data-testid="call-suggest-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du contact (ex. Prospect ABC)"
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
          <textarea data-testid="call-suggest-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={2} placeholder="Objectif de l'appel…"
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
          <button data-testid="call-suggest-run" onClick={run} disabled={loading || !name.trim() || !purpose.trim()}
            className="w-full bg-brand-blue text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors disabled:opacity-50">
            {loading ? "L'IA rédige…" : "Générer le script"}
          </button>
          {draft && (
            <div data-testid="call-suggest-draft" className="space-y-3 border-t border-slate-100 pt-4">
              <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Accroche</p>
                <p data-testid="call-draft-opening" className="text-sm text-brand-navy">{draft.opening}</p></div>
              <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Points à aborder</p>
                <ul data-testid="call-draft-points" className="text-sm text-slate-600 list-disc list-inside space-y-1">
                  {draft.points.map((p, i) => <li key={i}>{p}</li>)}
                </ul></div>
              <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Conclusion</p>
                <p data-testid="call-draft-closing" className="text-sm text-brand-navy">{draft.closing}</p></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RecordDialog = ({ cfg, initial, refData, onClose, onSaved }) => {
  const isEdit = Boolean(initial?.record_id);
  const [values, setValues] = useState(() => {
    const v = {};
    cfg.fields.forEach((f) => { v[f.name] = initial?.[f.name] ?? ""; });
    return v;
  });
  const [status, setStatus] = useState(initial?.status || cfg.statuses[0] || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = { ...values };
      if (cfg.statuses.length) payload.status = status;
      cfg.fields.forEach((f) => { if (f.ref && !payload[f.name]) payload[f.name] = null; });
      const res = isEdit
        ? await api.put(`/recruitment/${cfg.entity}/${initial.record_id}`, payload)
        : await api.post(`/recruitment/${cfg.entity}`, payload);
      onSaved(res.data);
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="rec-form-dialog" className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-brand-navy">{isEdit ? `Modifier — ${cfg.label}` : `Nouveau — ${cfg.label}`}</h2>
          <button data-testid="rec-form-close" onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-brand-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div data-testid="rec-form-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
          {cfg.fields.map((f) => (
            <div key={f.name}>
              <label className="text-sm font-medium text-brand-navy">{f.label}{f.required && " *"}</label>
              {f.type === "textarea" ? (
                <textarea data-testid={`rec-field-${f.name}`} value={values[f.name]} rows={3} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
              ) : f.type === "select" ? (
                <select data-testid={`rec-field-${f.name}`} value={values[f.name]} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise">
                  <option value="">—</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.ref ? (
                <select data-testid={`rec-field-${f.name}`} value={values[f.name] || ""} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise">
                  <option value="">— Aucun —</option>
                  {(refData[f.ref] || []).map((r) => <option key={r.record_id} value={r.record_id}>{f.refLabel(r)}</option>)}
                </select>
              ) : (
                <input data-testid={`rec-field-${f.name}`} type={f.type || "text"} value={values[f.name]} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
              )}
            </div>
          ))}
          {cfg.statuses.length > 0 && (
            <div>
              <label className="text-sm font-medium text-brand-navy">Statut</label>
              <select data-testid="rec-field-status" value={status} onChange={(e) => setStatus(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise">
                {cfg.statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
              </select>
            </div>
          )}
          <button data-testid="rec-save-button" onClick={save} disabled={saving}
            className="w-full bg-brand-blue text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors disabled:opacity-50">
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ModuleView = ({ cfg, fixedStatus, suggest }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(fixedStatus || "");
  const [dialog, setDialog] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [refData, setRefData] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      const effectiveStatus = fixedStatus || status;
      if (effectiveStatus) params.status = effectiveStatus;
      const res = await api.get(`/recruitment/${cfg.entity}`, { params });
      setRecords(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [cfg.entity, q, status, fixedStatus]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const refs = [...new Set(cfg.fields.filter((f) => f.ref).map((f) => f.ref))];
    refs.forEach((refEntity) => {
      api.get(`/recruitment/${refEntity}`)
        .then((r) => setRefData((prev) => ({ ...prev, [refEntity]: r.data })))
        .catch(() => {});
    });
  }, [cfg]);

  const remove = async (record) => {
    setError("");
    try {
      await api.delete(`/recruitment/${cfg.entity}/${record.record_id}`);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid={`rec-module-${cfg.entity}`} className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid={`rec-search-${cfg.entity}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…"
            className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
        </div>
        {cfg.statuses.length > 0 && !fixedStatus && (
          <select data-testid={`rec-status-filter-${cfg.entity}`} value={status} onChange={(e) => setStatus(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise">
            <option value="">Tous les statuts</option>
            {cfg.statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
          </select>
        )}
        {suggest && (
          <button data-testid="call-suggest-button" onClick={() => setSuggestOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-orange border border-brand-orange/40 rounded-lg px-4 py-2.5 hover:bg-brand-orange/10">
            <Sparkles className="w-4 h-4" /> Script IA
          </button>
        )}
        <button data-testid={`rec-new-${cfg.entity}`} onClick={() => setDialog({})}
          className="inline-flex items-center gap-1.5 bg-brand-blue text-white text-sm font-semibold rounded-lg px-4 py-2.5 hover:bg-brand-navy transition-colors">
          <Plus className="w-4 h-4" /> Nouveau
        </button>
      </div>

      {error && <div data-testid={`rec-error-${cfg.entity}`} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" /></div>
      ) : records.length === 0 ? (
        <div data-testid={`rec-empty-${cfg.entity}`} className="text-center py-16 text-slate-400 bg-white rounded-xl border border-slate-200">
          Aucune entrée pour le moment.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {records.map((r) => (
            <div key={r.record_id} data-testid={`rec-row-${r.record_id}`} className="flex flex-wrap items-center gap-3 px-5 py-3.5 border-b border-slate-50">
              <div className="flex-1 min-w-[200px]">
                <p className="font-medium text-brand-navy text-sm">{cfg.title(r) || "—"}</p>
                {cfg.subtitle(r) && <p className="text-xs text-slate-400 mt-0.5">{cfg.subtitle(r)}</p>}
              </div>
              <StatusBadge status={r.status} />
              <button data-testid={`rec-edit-${r.record_id}`} onClick={() => setDialog(r)} aria-label="Modifier"
                className="text-slate-400 hover:text-brand-blue transition-colors"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`rec-delete-${r.record_id}`} onClick={() => remove(r)} aria-label="Supprimer"
                className="text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {dialog !== null && (
        <RecordDialog cfg={cfg} initial={dialog.record_id ? dialog : null} refData={refData}
          onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
      )}
      {suggestOpen && <SuggestDialog onClose={() => setSuggestOpen(false)} />}
    </div>
  );
};

export default function Recruitment() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [module, setModule] = useState("command-center");
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/recruitment/overview/stats").catch((err) => {
      if ([401, 403].includes(err?.response?.status)) setForbidden(true);
    });
  }, [user]);

  const active = useMemo(() => MODULES.find((m) => m.key === module), [module]);

  if (!user) return null;

  return (
    <div data-testid="recruitment-page" className="min-h-screen bg-slate-50">
      <PublicNav />
      <div className="bg-brand-navy">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3">
            <Zap className="w-7 h-7 text-brand-turquoise" />
            Recrutement AI
          </h1>
          <p className="text-white/70 text-sm mt-1">CRM de recrutement LCS — réservé à l'équipe LCS et aux administrateurs NEXA.</p>
        </div>
      </div>

      {forbidden ? (
        <div data-testid="recruitment-forbidden" className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">
          Accès réservé à l'équipe LCS et aux administrateurs NEXA.
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col lg:flex-row gap-6">
          <nav className="lg:w-64 shrink-0 bg-white rounded-xl border border-slate-200 p-2 lg:self-start flex lg:flex-col gap-1 overflow-x-auto">
            {MODULES.map((m) => (
              <button
                key={m.key}
                data-testid={`rec-nav-${m.key}`}
                onClick={() => setModule(m.key)}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  module === m.key ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <m.icon className="w-4 h-4 shrink-0" />
                {m.label}
              </button>
            ))}
          </nav>
          <main className="flex-1 min-w-0">
            <h2 className="font-bold text-brand-navy text-lg mb-4 flex items-center gap-2">
              <active.icon className="w-5 h-5 text-brand-turquoise" />
              {active.label}
            </h2>
            {module === "command-center" && <CommandCenter />}
            {module === "autopilot" && <Autopilot />}
            {active.cfg && (
              <ModuleView
                key={module}
                cfg={ENTITIES[active.cfg]}
                fixedStatus={active.fixedStatus}
                suggest={active.suggest}
              />
            )}
          </main>
        </div>
      )}
    </div>
  );
}
